'use client'

import { useMemo, useState } from 'react'
import { Check, CircleAlert, Copy, Info, Plus, TriangleAlert, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'

import { UTM_MACROS_VERIFIED_ON, UTM_PRESETS } from './utm-presets'
import {
  GA4_EXTRA_UTM_KEYS,
  STANDARD_UTM_KEYS,
  buildUtmUrl,
  type ParamRow,
  type SpaceMode,
  type Warning,
} from './utm-url'

/**
 * /tools/utm-builder widget. Everything runs in the browser: nothing typed
 * here is sent anywhere. URL logic lives in utm-url.ts, macros in
 * utm-presets.ts.
 */

const FIELD_HELP: Record<string, { label: string; placeholder: string; hint: string }> = {
  utm_source: { label: 'utm_source', placeholder: 'facebook', hint: 'Where the click came from' },
  utm_medium: { label: 'utm_medium', placeholder: 'paid_social', hint: 'The channel type' },
  utm_campaign: { label: 'utm_campaign', placeholder: 'spring_sale', hint: 'The campaign' },
  utm_term: { label: 'utm_term', placeholder: 'running_shoes', hint: 'Keyword or audience' },
  utm_content: { label: 'utm_content', placeholder: 'video_a', hint: 'The creative or link' },
  utm_id: { label: 'utm_id', placeholder: 'campaign ID', hint: 'Campaign ID (GA4)' },
  utm_source_platform: {
    label: 'utm_source_platform',
    placeholder: 'Search Ads 360',
    hint: 'Buying platform (GA4)',
  },
  utm_creative_format: {
    label: 'utm_creative_format',
    placeholder: 'video',
    hint: 'Not reported in GA4 yet',
  },
  utm_marketing_tactic: {
    label: 'utm_marketing_tactic',
    placeholder: 'prospecting',
    hint: 'Not reported in GA4 yet',
  },
}

const EMPTY_VALUES: Record<string, string> = Object.fromEntries(
  [...STANDARD_UTM_KEYS, ...GA4_EXTRA_UTM_KEYS].map(key => [key, '']),
)

const WARNING_ICON = {
  error: CircleAlert,
  warn: TriangleAlert,
  info: Info,
} as const

function CopyText({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      type="button"
      size="sm"
      disabled={!value}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 1800)
        } catch {
          setCopied(false)
        }
      }}
      className="shrink-0 rounded-full bg-orange-500 font-semibold text-stone-950 hover:bg-orange-400"
    >
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

function Warnings({ warnings }: { warnings: Warning[] }) {
  if (warnings.length === 0) {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-emerald-400/25 bg-emerald-500/10 p-3 text-sm text-emerald-100">
        <Check className="size-4 shrink-0" aria-hidden /> No problems found.
      </p>
    )
  }
  return (
    <ul className="space-y-2">
      {warnings.map((warning, index) => {
        const Icon = WARNING_ICON[warning.level]
        return (
          <li
            key={`${index}-${warning.message}`}
            className={cn(
              'flex items-start gap-2 rounded-lg border p-3 text-sm leading-6',
              warning.level === 'error' && 'border-red-400/30 bg-red-500/10 text-red-100',
              warning.level === 'warn' && 'border-amber-400/30 bg-amber-500/10 text-amber-100',
              warning.level === 'info' && 'border-white/10 bg-white/[0.03] text-white/65',
            )}
          >
            <Icon className="mt-1 size-4 shrink-0" aria-hidden />
            <span>{warning.message}</span>
          </li>
        )
      })}
    </ul>
  )
}

export function UtmBuilderWidget() {
  const [baseUrl, setBaseUrl] = useState('https://example.com/shoes?color=red#reviews')
  const [values, setValues] = useState<Record<string, string>>({
    ...EMPTY_VALUES,
    utm_source: 'newsletter',
    utm_medium: 'email',
    utm_campaign: 'Spring Sale',
  })
  const [extras, setExtras] = useState<ParamRow[]>([])
  const [presetId, setPresetId] = useState('')
  const [lowercase, setLowercase] = useState(true)
  const [spaces, setSpaces] = useState<SpaceMode>('underscore')
  const [showGa4, setShowGa4] = useState(false)
  const [bulkNames, setBulkNames] = useState('Video A – Hook test\nVideo B – Price first\nStatic carousel')

  const preset = UTM_PRESETS.find(p => p.id === presetId)
  const options = { lowercase, spaces }

  const rows: ParamRow[] = useMemo(
    () => [
      ...[...STANDARD_UTM_KEYS, ...GA4_EXTRA_UTM_KEYS].map(key => ({
        key,
        value: values[key] ?? '',
      })),
      ...extras,
    ],
    [values, extras],
  )

  const result = buildUtmUrl(baseUrl, rows, options)

  const bulk = bulkNames
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .slice(0, 50)
    .map(name => ({
      name,
      url: buildUtmUrl(
        baseUrl,
        rows.map(row => (row.key === 'utm_content' ? { key: row.key, value: name } : row)),
        options,
      ).url,
    }))

  function applyPreset(id: string) {
    setPresetId(id)
    const next = UTM_PRESETS.find(p => p.id === id)
    if (!next) return
    setValues({ ...EMPTY_VALUES, ...next.values } as Record<string, string>)
    setExtras(next.extras.map(extra => ({ ...extra })))
    if (next.values.utm_id) setShowGa4(true)
  }

  const visibleKeys = showGa4
    ? [...STANDARD_UTM_KEYS, ...GA4_EXTRA_UTM_KEYS]
    : [...STANDARD_UTM_KEYS]

  return (
    <div className="grid gap-8 rounded-3xl border border-white/10 bg-white/[0.03] p-5 text-left sm:p-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="space-y-6">
        <div>
          <Label htmlFor="utm-base" className="text-sm text-white/80">
            Landing page URL
          </Label>
          <Input
            id="utm-base"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            placeholder="https://example.com/page"
            className="mt-2 border-white/15 bg-black/30 font-mono text-sm text-white"
            spellCheck={false}
          />
          <p className="mt-2 text-xs leading-5 text-white/45">
            Existing query parameters and #anchors are kept.
          </p>
        </div>

        <div>
          <Label className="text-sm text-white/80">Platform macros (optional)</Label>
          <ToggleGroup
            type="single"
            value={presetId}
            onValueChange={value => (value ? applyPreset(value) : setPresetId(''))}
            variant="outline"
            className="mt-3 grid w-full grid-cols-2 gap-2 sm:grid-cols-3"
          >
            {UTM_PRESETS.map(p => (
              <ToggleGroupItem
                key={p.id}
                value={p.id}
                className="h-auto justify-start rounded-lg border-white/15 px-3 py-2 text-left text-xs whitespace-normal data-[state=on]:border-orange-400 data-[state=on]:bg-orange-500/15 data-[state=on]:text-white"
              >
                {p.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {visibleKeys.map(key => {
            const help = FIELD_HELP[key]
            const required = key === 'utm_source' || key === 'utm_medium' || key === 'utm_campaign'
            return (
              <div key={key}>
                <Label htmlFor={`utm-${key}`} className="font-mono text-xs text-white/75">
                  {help.label}
                  {required ? <span className="text-orange-400"> *</span> : null}
                </Label>
                <Input
                  id={`utm-${key}`}
                  value={values[key] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                  placeholder={help.placeholder}
                  className="mt-1.5 border-white/15 bg-black/30 font-mono text-sm text-white"
                  spellCheck={false}
                />
                <p className="mt-1 text-xs text-white/40">{help.hint}</p>
              </div>
            )
          })}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowGa4(s => !s)}
          className="px-0 text-orange-400 hover:bg-transparent hover:text-orange-300"
        >
          {showGa4 ? 'Hide' : 'Show'} the four extra GA4 fields
        </Button>

        <div className="space-y-3">
          <Label className="text-sm text-white/80">Other parameters</Label>
          {extras.map((extra, index) => (
            <div key={index} className="flex gap-2">
              <Input
                aria-label="Parameter key"
                value={extra.key}
                onChange={e =>
                  setExtras(list =>
                    list.map((row, i) => (i === index ? { ...row, key: e.target.value } : row)),
                  )
                }
                placeholder="key"
                className="w-1/3 border-white/15 bg-black/30 font-mono text-sm text-white"
              />
              <Input
                aria-label="Parameter value"
                value={extra.value}
                onChange={e =>
                  setExtras(list =>
                    list.map((row, i) => (i === index ? { ...row, value: e.target.value } : row)),
                  )
                }
                placeholder="value"
                className="flex-1 border-white/15 bg-black/30 font-mono text-sm text-white"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove parameter"
                onClick={() => setExtras(list => list.filter((_, i) => i !== index))}
                className="text-white/50 hover:text-white"
              >
                <X className="size-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setExtras(list => [...list, { key: '', value: '' }])}
            className="rounded-full border-white/15 bg-transparent text-white/80"
          >
            <Plus className="size-4" /> Add parameter
          </Button>
        </div>

        <div className="space-y-4 rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <Switch id="utm-lowercase" checked={lowercase} onCheckedChange={setLowercase} />
            <Label htmlFor="utm-lowercase" className="text-sm text-white/80">
              Force lowercase
            </Label>
          </div>
          <div>
            <Label className="text-sm text-white/80">Spaces become</Label>
            <ToggleGroup
              type="single"
              value={spaces}
              onValueChange={value => value && setSpaces(value as SpaceMode)}
              variant="outline"
              className="mt-2"
            >
              <ToggleGroupItem value="underscore" className="px-3 font-mono text-xs">
                _
              </ToggleGroupItem>
              <ToggleGroupItem value="hyphen" className="px-3 font-mono text-xs">
                -
              </ToggleGroupItem>
              <ToggleGroupItem value="encode" className="px-3 font-mono text-xs">
                %20
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <p className="text-xs leading-5 text-white/45">
            UTM values are case-sensitive, so “Facebook” and “facebook” become two
            sources in your reports. Both rules apply to text you type only. Macros
            such as {'{{campaign.name}}'} are left untouched: they are filled in by
            the platform at click time with whatever the campaign is called there.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Tabs defaultValue="single">
          <TabsList>
            <TabsTrigger value="single">One URL</TabsTrigger>
            <TabsTrigger value="bulk">One per ad</TabsTrigger>
          </TabsList>
          <TabsContent value="single" className="mt-4 space-y-4">
            <div className="rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold tracking-wide text-white/50 uppercase">
                  Tagged URL
                </p>
                <CopyText value={result.url} label="Copy URL" />
              </div>
              <p
                className="mt-3 font-mono text-sm leading-6 break-all text-white"
                data-testid="utm-output"
              >
                {result.url || 'Enter a landing page URL'}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold tracking-wide text-white/50 uppercase">
                  Parameters only
                </p>
                <CopyText value={result.query} label="Copy" />
              </div>
              <p className="mt-3 font-mono text-sm leading-6 break-all text-white/80">
                {result.query || '—'}
              </p>
              <p className="mt-2 text-xs leading-5 text-white/45">
                For URL-parameter fields such as Meta’s URL parameters or a Google Ads
                final URL suffix.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="bulk" className="mt-4 space-y-4">
            <div>
              <Label htmlFor="utm-bulk" className="text-sm text-white/80">
                Ad or creative names, one per line
              </Label>
              <Textarea
                id="utm-bulk"
                value={bulkNames}
                onChange={e => setBulkNames(e.target.value)}
                rows={5}
                className="mt-2 border-white/15 bg-black/30 font-mono text-sm text-white"
              />
              <p className="mt-2 text-xs leading-5 text-white/45">
                Each line becomes utm_content, with the same case and space rules.
                Up to 50 lines.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold tracking-wide text-white/50 uppercase">
                  {bulk.length} URLs
                </p>
                <CopyText value={bulk.map(b => b.url).join('\n')} label="Copy all" />
              </div>
              <ul className="mt-3 space-y-3">
                {bulk.map((item, index) => (
                  <li key={`${index}-${item.name}`}>
                    <p className="text-xs text-white/45">{item.name}</p>
                    <p className="font-mono text-xs leading-5 break-all text-white/85">
                      {item.url || '—'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        <div>
          <p className="mb-3 text-sm font-medium text-white/80">Checks</p>
          <Warnings warnings={result.warnings} />
        </div>

        {preset ? (
          <div className="rounded-xl border border-white/10 p-4 text-sm leading-6 text-white/60">
            <p className="font-medium text-white/85">{preset.label}</p>
            <p className="mt-1">
              <span className="text-white/80">Where to paste:</span> {preset.pasteInto}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {preset.notes.map(note => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs font-semibold tracking-wide text-white/45 uppercase">
              Every documented macro
            </p>
            <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              {preset.macros.map(m => (
                <div key={m.macro} className="flex gap-2 text-xs">
                  <dt className="font-mono text-orange-300">{m.macro}</dt>
                  <dd className="text-white/50">{m.meaning}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-white/40">
              Source:{' '}
              <a
                href={preset.source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 underline-offset-4 hover:underline"
              >
                {preset.source.label}
              </a>
              , checked {UTM_MACROS_VERIFIED_ON}.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}
